import { getLayer } from '../layerService.js';
import {
    applyGraphDiff,
    canComposeGraphDiffs,
    composeGraphDiffs,
    graphDiffObjectUuids,
} from '../../graph/graphDiff.js';

const MERGE_WINDOW_MS = 1000;

/**
 * One edit of a page's node graph `page[graphKey]` (e.g. `logic`). Stores an
 * id-based diff (see graphDiff.js), so it can be undone independently of
 * other edits.
 *
 * Consecutive edits with the same `mergeKey` (e.g. typing in one node's
 * property field) coalesce into one entry.
 */
export default class GraphCommand {
    constructor({ graphKey, pageUuid, diff, description = 'Edit graph', mergeKey = null, category = 'structure', type = 'Graph' }) {
        this.id = crypto.randomUUID();
        this.timestamp = Date.now();
        this.description = description;
        this.category = category;
        this.type = type;
        this.graphKey = graphKey;
        this.pageUuid = pageUuid;
        this.diff = diff;
        this.mergeKey = mergeKey;
        this.affectedUuids = graphDiffObjectUuids(diff);
    }

    _apply(backward) {
        const page = getLayer()?.findOne?.('#' + this.pageUuid);
        if (!page) return;
        page[this.graphKey] = applyGraphDiff(page[this.graphKey], this.diff, { backward });
        page.getLayer?.()?.batchDraw?.();
    }

    execute() {
        this._apply(false);
    }

    undo() {
        this._apply(true);
    }

    canMergeWith(other) {
        return other instanceof GraphCommand &&
            other.graphKey === this.graphKey &&
            this.mergeKey !== null && other.mergeKey === this.mergeKey &&
            other.pageUuid === this.pageUuid &&
            other.timestamp - this.timestamp < MERGE_WINDOW_MS &&
            canComposeGraphDiffs(this.diff, other.diff);
    }

    mergeWith(other) {
        this.diff = composeGraphDiffs(this.diff, other.diff);
        this.timestamp = other.timestamp;
        this.affectedUuids = graphDiffObjectUuids(this.diff);
    }

    toJSON() {
        return {
            type: this.type,
            id: this.id,
            timestamp: this.timestamp,
            payload: { pageUuid: this.pageUuid, diff: this.diff, description: this.description },
        };
    }
}
